import { useRef, useState, useEffect, lazy, Suspense } from 'react';
import { Hero } from './components/Hero';
import { TheInsight } from './components/TheInsight';

import { Exploring } from './components/Exploring';
import { ResearchTerminal } from './components/ResearchTerminal';
import { setupAnalytics } from './utils/analytics';

// ANIMATION - Lazy loaded for performance (~600KB Three.js deferred)
const AnimationV5_Enhanced = lazy(() => import('./components/animations/v1/AnimationV5_Enhanced'));

function App() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [animationOpacity, setAnimationOpacity] = useState(0.5);

  // Initialize analytics on mount
  useEffect(() => {
    setupAnalytics();
  }, []);

  // Animation gets brighter as user scrolls (50% → 75%)
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const maxScroll = 1000;
      const minOpacity = 0.5;
      const maxOpacity = 0.75;

      const progress = Math.min(scrollY / maxScroll, 1);
      const newOpacity = minOpacity + (progress * (maxOpacity - minOpacity));
      setAnimationOpacity(newOpacity);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative min-h-screen bg-black text-white overflow-hidden" ref={scrollContainerRef}>

      {/* Background Animation Layer - Lazy loaded, scroll-fading opacity */}
      <div
        className="fixed inset-0 z-0 mix-blend-lighten pointer-events-auto"
        style={{ opacity: animationOpacity }}
      >
        <Suspense fallback={<div className="w-full h-full bg-black" />}>
          <AnimationV5_Enhanced />
        </Suspense>
      </div>

      {/* Content Layer */}
      <div className="relative z-10 pointer-events-none">
        <div className="pointer-events-auto">
          {/* Hero: KEMPION + animated tagline (transparent — animation shows through) */}
          <Hero />

          {/* Translucent backdrop for text sections */}
          <div className="relative">
            {/* Gradient fade from transparent to backdrop */}
            <div
              className="h-32 pointer-events-none"
              style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.8))' }}
            />

            <div style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
              {/* Research Terminal: Live system brief + chat */}
              <ResearchTerminal />

              {/* Exploring: Research areas */}
              <Exploring />

              {/* The Insight: "A simple observation" */}
              <TheInsight />

              {/* Footer */}
              <footer className="py-24 md:py-32 px-6 md:px-24">
            <div className="max-w-2xl mx-auto">
              {/* Bottom bar */}
              <div className="mt-24 pt-8 border-t border-gray-900 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <img src="/favicon.svg" alt="" className="w-4 h-4 opacity-30" />
                  <p className="text-[10px] text-gray-700 font-mono tracking-widest uppercase">
                    &copy; {new Date().getFullYear()} Kempion Research
                  </p>
                </div>
                <p className="text-xs text-gray-600 italic">
                  Most AI performs certainty. Ours is learning to earn it.
                </p>
              </div>
            </div>
          </footer>
            </div>{/* end bg-black backdrop */}
          </div>{/* end translucent wrapper */}
        </div>
      </div>

    </div>
  );
}

export default App;
